/**
 * Chemicals Industry Configuration
 *
 * Supports: Reactors, Distillation Columns, Heat Exchangers, Compressors, Pumps, Storage Tanks
 */

import { IndustryConfig } from '../industryTypes'

export const chemicalsConfig: IndustryConfig = {
  id: 'chemicals',
  name: 'Chemicals',
  description: 'Chemical processing and petrochemical plant monitoring',
  icon: 'FlaskConical',

  terminology: {
    asset: 'Process Unit',
    assets: 'Process Units',
    assetPlural: 'Process Units',
    assetType: 'Unit Type',
    failure: 'Process Upset',
    health: 'Status',
    healthScore: 'Process Health Index',
    healthy: 'Normal',
    degraded: 'Off-Spec',
    critical: 'Shutdown',
    alert: 'Process Alert',
    process: 'Production',
    maintenance: 'Maintenance',
    inspection: 'Turnaround',
    downtime: 'Outage',
    workOrder: 'Work Order',
    prediction: 'Forecast',
    anomaly: 'Deviation',
    trend: 'Trend',
    risk: 'Process Risk',
    facility: 'Plant',
    facilityPlural: 'Plants',
    location: 'Unit',
    operator: 'Control Room Operator',
    supervisor: 'Shift Supervisor',
    calibration: 'Instrument Calibration',
    compliance: 'Regulatory Compliance',
    criticality: 'Process Criticality',
  },

  assetTypes: [
    {
      id: 'reactor',
      name: 'Reactor',
      icon: 'FlaskConical',
      description: 'Chemical reaction vessels including batch and continuous reactors',
      category: 'Reaction',
      defaultMetrics: ['temperature', 'pressure', 'agitator_speed', 'conversion_rate'],
      maintenanceTypes: ['catalyst_change', 'vessel_inspection', 'agitator_service', 'instrumentation_calibration'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary production reactor', downtimeCostMultiplier: 15 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Key process reactor', downtimeCostMultiplier: 8 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary reactor', downtimeCostMultiplier: 4 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Backup or auxiliary reactor', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'distillation_column',
      name: 'Distillation Column',
      icon: 'Columns3',
      description: 'Separation towers for product purification and fractionation',
      category: 'Separation',
      defaultMetrics: ['top_temperature', 'bottom_temperature', 'reflux_ratio', 'differential_pressure'],
      maintenanceTypes: ['tray_inspection', 'packing_replacement', 'reboiler_service', 'condenser_cleaning'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Main product column', downtimeCostMultiplier: 12 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Primary separation unit', downtimeCostMultiplier: 6 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Secondary column', downtimeCostMultiplier: 3 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Auxiliary purification', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'heat_exchanger',
      name: 'Heat Exchanger',
      icon: 'ArrowLeftRight',
      description: 'Shell and tube, plate, and air-cooled heat transfer equipment',
      category: 'Heat Transfer',
      defaultMetrics: ['inlet_temperature', 'outlet_temperature', 'pressure_drop', 'heat_duty'],
      maintenanceTypes: ['tube_cleaning', 'gasket_replacement', 'leak_test', 'tube_bundle_replacement'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Process critical exchanger', downtimeCostMultiplier: 10 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Major heat duty unit', downtimeCostMultiplier: 5 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Standard service', downtimeCostMultiplier: 2 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Non-critical duty', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'compressor',
      name: 'Compressor',
      icon: 'Wind',
      description: 'Reciprocating, centrifugal, and screw compressors for gas handling',
      category: 'Rotating Equipment',
      defaultMetrics: ['discharge_pressure', 'suction_pressure', 'vibration', 'bearing_temperature'],
      maintenanceTypes: ['valve_replacement', 'seal_replacement', 'bearing_inspection', 'alignment_check'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Process gas compressor', downtimeCostMultiplier: 20 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Main air or utility compressor', downtimeCostMultiplier: 10 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Backup compressor', downtimeCostMultiplier: 4 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Auxiliary service', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'pump',
      name: 'Pump',
      icon: 'Droplets',
      description: 'Centrifugal, positive displacement, and specialty pumps',
      category: 'Rotating Equipment',
      defaultMetrics: ['discharge_pressure', 'flow_rate', 'vibration', 'motor_current'],
      maintenanceTypes: ['seal_replacement', 'impeller_inspection', 'bearing_replacement', 'alignment'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Single-train critical pump', downtimeCostMultiplier: 12 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Primary process pump', downtimeCostMultiplier: 6 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Spared pump', downtimeCostMultiplier: 2 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Utility or transfer pump', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'storage_tank',
      name: 'Storage Tank',
      icon: 'Container',
      description: 'Atmospheric and pressurized storage vessels',
      category: 'Storage',
      defaultMetrics: ['level', 'temperature', 'pressure', 'vapor_concentration'],
      maintenanceTypes: ['internal_inspection', 'coating_repair', 'roof_seal_replacement', 'cathodic_protection_check'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Hazardous material storage', downtimeCostMultiplier: 8 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Primary feedstock storage', downtimeCostMultiplier: 4 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Intermediate storage', downtimeCostMultiplier: 2 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Utility storage', downtimeCostMultiplier: 1 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'process_safety',
      name: 'Process Safety',
      icon: 'ShieldAlert',
      description: 'Safety performance indicators for chemical process operations',
      businessValue: 'Prevent incidents, protect personnel, and ensure regulatory compliance',
      category: 'industry_specific',
      metrics: [
        { id: 'psr_tier1', name: 'Tier 1 PSE Rate', unit: 'events/yr', description: 'Process safety events with major release', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 0, warning: 1, critical: 2 } },
        { id: 'psr_tier2', name: 'Tier 2 PSE Rate', unit: 'events/yr', description: 'Process safety events with minor release', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 2, warning: 5, critical: 10 } },
        { id: 'near_miss_rate', name: 'Near Miss Rate', unit: 'events/mo', description: 'Near miss incidents reported', dataType: 'continuous', aggregation: 'sum', goodDirection: 'target', defaultThresholds: { healthy: 10, warning: 5, critical: 2 } },
        { id: 'sif_demand_rate', name: 'SIF Demand Rate', unit: 'demands/yr', description: 'Safety instrumented function activations', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 5, warning: 10, critical: 20 } },
      ],
      thresholds: { healthy: { min: 95 }, warning: { min: 85, max: 95 }, critical: { max: 85 } },
      visualizations: ['gauge', 'trend', 'bar_chart', 'heatmap'],
    },
    {
      id: 'production_efficiency',
      name: 'Production Efficiency',
      icon: 'Gauge',
      description: 'Production throughput and efficiency metrics',
      businessValue: 'Maximize output and minimize operational costs',
      category: 'universal',
      metrics: [
        { id: 'throughput', name: 'Throughput', unit: 'MT/day', description: 'Daily production rate', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 1000, warning: 800, critical: 600 } },
        { id: 'yield', name: 'Process Yield', unit: '%', description: 'Product output vs feedstock input', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 90, critical: 85 } },
        { id: 'energy_intensity', name: 'Energy Intensity', unit: 'GJ/MT', description: 'Energy consumed per unit of product', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 3, warning: 4, critical: 5 } },
        { id: 'uptime', name: 'Plant Uptime', unit: '%', description: 'Operational time vs scheduled time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 90, critical: 85 } },
      ],
      thresholds: { healthy: { min: 90 }, warning: { min: 80, max: 90 }, critical: { max: 80 } },
      visualizations: ['line_chart', 'gauge', 'trend', 'comparison'],
    },
    {
      id: 'quality_control',
      name: 'Quality Control',
      icon: 'TestTube',
      description: 'Product quality and specification adherence',
      businessValue: 'Ensure product meets specifications and reduce off-spec production',
      category: 'industry_specific',
      metrics: [
        { id: 'on_spec_rate', name: 'On-Spec Rate', unit: '%', description: 'Production meeting specifications', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 95, critical: 90 } },
        { id: 'purity', name: 'Product Purity', unit: '%', description: 'Main product concentration', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 99.5, warning: 99, critical: 98 } },
        { id: 'impurity_level', name: 'Impurity Level', unit: 'ppm', description: 'Contaminant concentration', dataType: 'continuous', aggregation: 'max', goodDirection: 'lower', defaultThresholds: { healthy: 50, warning: 100, critical: 200 } },
        { id: 'batch_rejection', name: 'Batch Rejection Rate', unit: '%', description: 'Batches requiring rework or disposal', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 1, warning: 3, critical: 5 } },
      ],
      thresholds: { healthy: { min: 95 }, warning: { min: 90, max: 95 }, critical: { max: 90 } },
      visualizations: ['bar_chart', 'trend', 'distribution', 'comparison'],
    },
    {
      id: 'environmental',
      name: 'Environmental Compliance',
      icon: 'Leaf',
      description: 'Emissions, effluents, and environmental performance',
      businessValue: 'Maintain regulatory compliance and reduce environmental impact',
      category: 'industry_specific',
      metrics: [
        { id: 'voc_emissions', name: 'VOC Emissions', unit: 'kg/hr', description: 'Volatile organic compound releases', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 5, warning: 10, critical: 15 } },
        { id: 'wastewater_quality', name: 'Wastewater BOD', unit: 'mg/L', description: 'Biological oxygen demand of effluent', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 20, warning: 30, critical: 50 } },
        { id: 'ghg_intensity', name: 'GHG Intensity', unit: 'tCO2e/MT', description: 'Greenhouse gas emissions per product', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 0.5, warning: 0.8, critical: 1.2 } },
        { id: 'permit_exceedances', name: 'Permit Exceedances', unit: 'events/yr', description: 'Environmental permit violations', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 0, warning: 1, critical: 3 } },
      ],
      thresholds: { healthy: { min: 90 }, warning: { min: 80, max: 90 }, critical: { max: 80 } },
      visualizations: ['line_chart', 'bar_chart', 'trend', 'heatmap'],
    },
  ],

  maintenanceTypes: [
    {
      id: 'preventive',
      name: 'Preventive Maintenance',
      description: 'Scheduled time-based or usage-based maintenance',
      category: 'preventive',
      defaultInterval: { value: 2000, unit: 'hours' },
      laborRequirements: [
        { role: 'Instrument Technician', count: 1, hourlyRate: 55 },
        { role: 'Maintenance Technician', count: 1, hourlyRate: 50 },
      ],
      estimatedDuration: { min: 2, typical: 4, max: 8, unit: 'hours' },
      skillsRequired: ['mechanical', 'instrumentation', 'process_safety'],
      partsCommonly: ['gaskets', 'filters', 'lubricants', 'calibration_gases'],
    },
    {
      id: 'corrective',
      name: 'Corrective Maintenance',
      description: 'Repair after equipment failure or process upset',
      category: 'corrective',
      laborRequirements: [
        { role: 'Maintenance Technician', count: 2, hourlyRate: 50 },
        { role: 'Process Engineer', count: 1, hourlyRate: 85 },
      ],
      estimatedDuration: { min: 4, typical: 12, max: 48, unit: 'hours' },
      skillsRequired: ['troubleshooting', 'mechanical', 'electrical', 'process'],
      partsCommonly: ['seals', 'bearings', 'valves', 'instruments'],
    },
    {
      id: 'predictive',
      name: 'Predictive Maintenance',
      description: 'Condition-based maintenance triggered by monitoring data',
      category: 'predictive',
      laborRequirements: [
        { role: 'Reliability Engineer', count: 1, hourlyRate: 90 },
        { role: 'Instrument Technician', count: 1, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 2, typical: 6, max: 16, unit: 'hours' },
      skillsRequired: ['vibration_analysis', 'thermography', 'oil_analysis', 'process_data_analysis'],
      partsCommonly: ['bearings', 'seals', 'couplings', 'filters'],
    },
    {
      id: 'turnaround',
      name: 'Turnaround',
      description: 'Planned shutdown for major overhaul and inspection',
      category: 'condition_based',
      defaultInterval: { value: 48, unit: 'months' },
      laborRequirements: [
        { role: 'Project Manager', count: 1, hourlyRate: 100 },
        { role: 'Process Engineer', count: 3, hourlyRate: 85 },
        { role: 'Maintenance Technician', count: 20, hourlyRate: 50 },
        { role: 'Contract Labor', count: 50, hourlyRate: 45 },
      ],
      estimatedDuration: { min: 14, typical: 28, max: 42, unit: 'days' },
      skillsRequired: ['project_management', 'mechanical', 'electrical', 'instrumentation', 'welding', 'inspection'],
      partsCommonly: ['catalyst', 'internals', 'heat_exchanger_tubes', 'vessel_internals', 'gaskets', 'valves'],
    },
  ],

  riskFactors: [
    { id: 'safety', name: 'Safety Risk', description: 'Risk of personnel injury or process safety incident', category: 'safety', weight: 0.35, calculationMethod: 'threshold', inputs: ['sif_status', 'alarm_rate', 'near_miss_history', 'hazard_level'] },
    { id: 'environmental', name: 'Environmental Risk', description: 'Risk of environmental release or permit exceedance', category: 'compliance', weight: 0.25, calculationMethod: 'linear', inputs: ['emissions_trend', 'containment_status', 'waste_handling'] },
    { id: 'operational', name: 'Operational Risk', description: 'Risk of unplanned downtime or production loss', category: 'operational', weight: 0.25, calculationMethod: 'exponential', inputs: ['equipment_health', 'redundancy_status', 'maintenance_backlog'] },
    { id: 'regulatory', name: 'Regulatory Risk', description: 'Risk of regulatory non-compliance or citation', category: 'compliance', weight: 0.15, calculationMethod: 'threshold', inputs: ['permit_status', 'inspection_findings', 'corrective_actions'] },
  ],

  complianceStandards: [
    {
      id: 'osha_psm',
      name: 'OSHA Process Safety Management',
      shortName: 'OSHA PSM',
      description: 'Process safety management for highly hazardous chemicals',
      authority: 'US Department of Labor - OSHA',
      region: ['US'],
      requirements: [
        { id: 'phi', name: 'Process Hazard Analysis', description: 'Regular PHA updates and revalidation', kpiId: 'pha_status', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'mot', name: 'Management of Change', description: 'MOC process compliance', kpiId: 'moc_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'mi', name: 'Mechanical Integrity', description: 'Equipment inspection and testing program', kpiId: 'mi_compliance', threshold: 95, operator: 'gte', severity: 'required' },
        { id: 'hot_work', name: 'Hot Work Permit', description: 'Hot work permit compliance', kpiId: 'hot_work_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'annual',
    },
    {
      id: 'epa_rmp',
      name: 'EPA Risk Management Program',
      shortName: 'EPA RMP',
      description: 'Risk management planning for facilities with regulated substances',
      authority: 'US Environmental Protection Agency',
      region: ['US'],
      requirements: [
        { id: 'rmp_plan', name: 'RMP Plan', description: 'Current RMP plan submission', kpiId: 'rmp_plan_status', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'worst_case', name: 'Worst Case Scenario', description: 'Worst case scenario analysis current', kpiId: 'worst_case_current', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'emergency_response', name: 'Emergency Response', description: 'Emergency response coordination', kpiId: 'emergency_response_ready', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'annual',
    },
    {
      id: 'iso_14001',
      name: 'ISO 14001 Environmental Management',
      shortName: 'ISO 14001',
      description: 'Environmental management system standard',
      authority: 'ISO',
      region: ['Global'],
      requirements: [
        { id: 'ems', name: 'Environmental Management System', description: 'Documented EMS implementation', kpiId: 'ems_status', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'aspects', name: 'Environmental Aspects', description: 'Identification and control of environmental aspects', kpiId: 'aspects_identified', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'objectives', name: 'Environmental Objectives', description: 'Progress toward environmental objectives', kpiId: 'objectives_progress', threshold: 80, operator: 'gte', severity: 'advisory' },
        { id: 'audits', name: 'Internal Audits', description: 'Regular internal audit completion', kpiId: 'audit_completion', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'annual',
    },
  ],

  visualization: {
    primaryColor: '#10b981',
    secondaryColor: '#ef4444',
    healthyColor: '#10b981',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  narrativeTemplates: [
    {
      id: 'health_summary',
      name: 'Status Summary',
      context: 'asset_health',
      audience: ['executive', 'manager', 'engineer'],
      template: 'Your {assetType} "{assetName}" is currently {healthStatus}. {trendDescription} Over the past {timeRange}, {kpiSummary}. {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'timeRange', type: 'time' },
        { name: 'kpiSummary', type: 'metric' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
    {
      id: 'prediction_alert',
      name: 'Forecast Alert',
      context: 'prediction',
      audience: ['manager', 'engineer', 'technician'],
      template: 'AI {prediction} for {assetType} "{assetName}": {predictionDetails}. Confidence: {confidence}%. Based on analysis of {dataPoints} data points. Recommended action: {recommendation}',
      variables: [
        { name: 'prediction', type: 'terminology' },
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'predictionDetails', type: 'metric' },
        { name: 'confidence', type: 'percentage' },
        { name: 'dataPoints', type: 'metric' },
        { name: 'recommendation', type: 'metric' },
      ],
    },
    {
      id: 'deviation_alert',
      name: 'Deviation Alert',
      context: 'anomaly',
      audience: ['operator', 'engineer'],
      template: '{anomaly} detected on {assetType} "{assetName}": {anomalyDetails}. Current value: {currentValue}. Expected range: {expectedRange}. Immediate action: {immediateAction}',
      variables: [
        { name: 'anomaly', type: 'terminology' },
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'anomalyDetails', type: 'metric' },
        { name: 'currentValue', type: 'metric' },
        { name: 'expectedRange', type: 'metric' },
        { name: 'immediateAction', type: 'metric' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: '24h',
    refreshInterval: 15,
    defaultDashboard: 'command-center',
  },

  // =============================================================================
  // PROFESSIONAL ROLES (Chemicals Industry)
  // =============================================================================
  professionalRoles: [
    // SME Roles
    {
      id: 'process_engineer',
      category: 'sme',
      name: 'Process Engineer',
      shortName: 'PE',
      description: 'Optimizes chemical processes for yield, efficiency, and safety',
      responsibilities: [
        'Process optimization and troubleshooting',
        'Heat and mass balance analysis',
        'Process simulation and modeling',
        'New process development',
        'Operating procedure development',
      ],
      focusAreas: ['yield', 'energy_efficiency', 'reaction_kinetics', 'separation_efficiency', 'process_safety'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items', 'business_impact'],
      },
      decisionAuthority: ['process_parameters', 'operating_limits', 'recipe_changes'],
      reportingTo: 'Operations Manager',
      icon: 'FlaskConical',
    },
    {
      id: 'reliability_engineer',
      category: 'analyst',
      name: 'Reliability Engineer',
      shortName: 'RE',
      description: 'Ensures equipment reliability and drives continuous improvement',
      responsibilities: [
        'Root cause failure analysis',
        'Reliability modeling and prediction',
        'Maintenance strategy optimization',
        'Spare parts criticality analysis',
        'Turnaround planning support',
      ],
      focusAreas: ['mtbf', 'mttr', 'availability', 'failure_modes', 'risk_assessment'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'risk', 'action_items'],
      },
      decisionAuthority: ['maintenance_strategy', 'spare_parts_stocking', 'equipment_modifications'],
      reportingTo: 'Maintenance Manager',
      icon: 'Target',
    },
    // Technician Roles
    {
      id: 'instrument_technician',
      category: 'technician',
      name: 'Instrument Technician',
      shortName: 'IT',
      description: 'Maintains and calibrates process instrumentation and control systems',
      responsibilities: [
        'Instrument calibration and maintenance',
        'Control loop tuning',
        'Analyzer maintenance',
        'Safety instrumented system testing',
        'DCS/PLC troubleshooting',
      ],
      focusAreas: ['calibration_status', 'control_performance', 'analyzer_reliability', 'sis_testing'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items'],
      },
      decisionAuthority: ['calibration_methods', 'loop_tuning', 'instrument_selection'],
      reportingTo: 'Instrumentation Supervisor',
      icon: 'Gauge',
    },
    {
      id: 'rotating_equipment_technician',
      category: 'technician',
      name: 'Rotating Equipment Technician',
      shortName: 'RET',
      description: 'Specializes in pumps, compressors, and rotating machinery maintenance',
      responsibilities: [
        'Pump and compressor overhauls',
        'Seal replacements',
        'Alignment and balancing',
        'Vibration monitoring',
        'Bearing inspections',
      ],
      focusAreas: ['vibration', 'alignment', 'bearing_condition', 'seal_performance', 'lubrication'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items'],
      },
      decisionAuthority: ['repair_methods', 'parts_selection', 'alignment_tolerances'],
      reportingTo: 'Maintenance Supervisor',
      icon: 'Cog',
    },
    // Executive Roles
    {
      id: 'plant_manager',
      category: 'executive',
      name: 'Plant Manager',
      shortName: 'PM',
      description: 'Oversees entire plant operations, safety, and financial performance',
      responsibilities: [
        'Overall plant performance',
        'Safety and environmental leadership',
        'Budget and cost management',
        'Regulatory compliance',
        'Strategic planning',
      ],
      focusAreas: ['safety_performance', 'production_targets', 'cost_per_unit', 'environmental_compliance', 'reliability'],
      explanationStyle: {
        depth: 'high_level',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive',
        focusOn: ['business_impact', 'risk', 'compliance'],
      },
      decisionAuthority: ['capital_expenditure', 'staffing_levels', 'strategic_initiatives', 'emergency_response'],
      reportingTo: 'VP Operations',
      icon: 'Building',
    },
    {
      id: 'operations_manager',
      category: 'executive',
      name: 'Operations Manager',
      shortName: 'OM',
      description: 'Manages day-to-day plant operations and production targets',
      responsibilities: [
        'Daily production management',
        'Shift coordination',
        'Process troubleshooting support',
        'Operating cost control',
        'Product quality oversight',
      ],
      focusAreas: ['throughput', 'yield', 'energy_consumption', 'product_quality', 'shift_performance'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['business_impact', 'action_items', 'risk'],
      },
      decisionAuthority: ['production_scheduling', 'operating_rates', 'shift_assignments'],
      reportingTo: 'Plant Manager',
      icon: 'Users',
    },
  ],

  // =============================================================================
  // PARTS CATALOG (Chemicals Industry)
  // =============================================================================
  partsCatalog: {
    categories: [
      {
        id: 'rotating_equipment',
        name: 'Rotating Equipment',
        description: 'Pumps, compressors, and rotating machinery components',
        parts: [
          {
            id: 'mech_seal_api_682',
            partNumber: 'FLOWSERVE-ISC2BB',
            name: 'API 682 Mechanical Seal',
            description: 'Double cartridge seal for API pumps, Plan 53B',
            category: 'rotating_equipment',
            manufacturer: 'Flowserve',
            unitCost: 4500.00,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['pump'],
            failureModes: ['seal_leak', 'seal_face_wear', 'bellows_failure'],
            averageLifespan: { value: 3, unit: 'years' },
            specifications: { sealType: 'Double', plan: '53B', material: 'Silicon Carbide' },
          },
          {
            id: 'bearing_angular_contact',
            partNumber: 'SKF-7316-BECBM',
            name: 'Angular Contact Ball Bearing',
            description: 'Heavy-duty angular contact bearing for compressors',
            category: 'rotating_equipment',
            manufacturer: 'SKF',
            unitCost: 890.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['compressor', 'pump'],
            failureModes: ['bearing_wear', 'vibration_high', 'overheating'],
            averageLifespan: { value: 4, unit: 'years' },
            specifications: { bore: '80mm', od: '170mm', width: '39mm' },
          },
          {
            id: 'coupling_disc',
            partNumber: 'REXNORD-THOMAS-DBZ',
            name: 'Disc Coupling Assembly',
            description: 'Flexible disc coupling for high-speed applications',
            category: 'rotating_equipment',
            manufacturer: 'Rexnord',
            unitCost: 2200.00,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['compressor', 'pump'],
            failureModes: ['coupling_wear', 'disc_crack', 'hub_loosening'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { type: 'Disc', torque: '15000 Nm', speed: '5000 RPM' },
          },
          {
            id: 'impeller_ss316',
            partNumber: 'SULZER-IMP-316L',
            name: 'Pump Impeller SS316L',
            description: 'Stainless steel closed impeller for corrosive service',
            category: 'rotating_equipment',
            manufacturer: 'Sulzer',
            unitCost: 3800.00,
            currency: 'USD',
            leadTime: { value: 21, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['pump'],
            failureModes: ['erosion', 'corrosion', 'cavitation_damage'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { material: 'SS316L', diameter: '12 inch', type: 'Closed' },
          },
        ],
      },
      {
        id: 'instrumentation',
        name: 'Instrumentation',
        description: 'Process instruments, transmitters, and analyzers',
        parts: [
          {
            id: 'pressure_transmitter',
            partNumber: 'ROSEMOUNT-3051S',
            name: 'Pressure Transmitter',
            description: 'Smart pressure transmitter with HART protocol',
            category: 'instrumentation',
            manufacturer: 'Emerson',
            unitCost: 2400.00,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['reactor', 'distillation_column', 'compressor', 'pump', 'storage_tank'],
            failureModes: ['drift', 'diaphragm_failure', 'electronics_failure'],
            averageLifespan: { value: 10, unit: 'years' },
            specifications: { range: '0-300 psig', accuracy: '0.04%', protocol: 'HART' },
          },
          {
            id: 'temperature_transmitter',
            partNumber: 'ROSEMOUNT-3144P',
            name: 'Temperature Transmitter',
            description: 'Dual-input temperature transmitter with diagnostics',
            category: 'instrumentation',
            manufacturer: 'Emerson',
            unitCost: 1800.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['reactor', 'distillation_column', 'heat_exchanger'],
            failureModes: ['drift', 'sensor_failure', 'electronics_failure'],
            averageLifespan: { value: 10, unit: 'years' },
            specifications: { inputs: 'Dual RTD/TC', accuracy: '0.1C', protocol: 'HART' },
          },
          {
            id: 'level_transmitter_radar',
            partNumber: 'VEGA-VEGAPULS64',
            name: 'Radar Level Transmitter',
            description: 'Non-contact radar for corrosive and high-temperature service',
            category: 'instrumentation',
            manufacturer: 'VEGA',
            unitCost: 3200.00,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['reactor', 'storage_tank', 'distillation_column'],
            failureModes: ['antenna_fouling', 'electronics_failure', 'false_echo'],
            averageLifespan: { value: 15, unit: 'years' },
            specifications: { frequency: '80 GHz', range: '30m', accuracy: '2mm' },
          },
          {
            id: 'flow_transmitter_coriolis',
            partNumber: 'MICROMOTION-F200',
            name: 'Coriolis Flow Meter',
            description: 'Mass flow and density measurement',
            category: 'instrumentation',
            manufacturer: 'Emerson',
            unitCost: 12000.00,
            currency: 'USD',
            leadTime: { value: 21, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['reactor', 'distillation_column', 'storage_tank'],
            failureModes: ['tube_coating', 'tube_erosion', 'electronics_failure'],
            averageLifespan: { value: 20, unit: 'years' },
            specifications: { size: '2 inch', accuracy: '0.1%', materials: 'SS316L' },
          },
        ],
      },
      {
        id: 'valves',
        name: 'Valves',
        description: 'Control valves, isolation valves, and safety valves',
        parts: [
          {
            id: 'control_valve_globe',
            partNumber: 'FISHER-GX',
            name: 'Globe Control Valve',
            description: 'Equal percentage globe valve with digital positioner',
            category: 'valves',
            manufacturer: 'Emerson',
            unitCost: 8500.00,
            currency: 'USD',
            leadTime: { value: 28, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['reactor', 'distillation_column', 'heat_exchanger'],
            failureModes: ['packing_leak', 'seat_erosion', 'actuator_failure'],
            averageLifespan: { value: 15, unit: 'years' },
            specifications: { size: '4 inch', class: '300', trim: 'Equal%' },
          },
          {
            id: 'ball_valve_trunnion',
            partNumber: 'CAMERON-T31',
            name: 'Trunnion Ball Valve',
            description: 'Full-bore trunnion mounted ball valve',
            category: 'valves',
            manufacturer: 'Cameron',
            unitCost: 15000.00,
            currency: 'USD',
            leadTime: { value: 42, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['reactor', 'storage_tank', 'compressor'],
            failureModes: ['seat_leak', 'stem_seal_leak', 'ball_damage'],
            averageLifespan: { value: 25, unit: 'years' },
            specifications: { size: '8 inch', class: '600', bore: 'Full' },
          },
          {
            id: 'safety_relief_valve',
            partNumber: 'CONSOLIDATED-1900',
            name: 'Pressure Safety Valve',
            description: 'API 526 safety relief valve',
            category: 'valves',
            manufacturer: 'Dresser',
            unitCost: 4200.00,
            currency: 'USD',
            leadTime: { value: 21, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['reactor', 'distillation_column', 'compressor', 'storage_tank'],
            failureModes: ['seat_leak', 'spring_failure', 'chatter'],
            averageLifespan: { value: 10, unit: 'years' },
            specifications: { size: '3x4 inch', setPoint: '150 psig', material: 'SS316' },
          },
          {
            id: 'check_valve_swing',
            partNumber: 'VELAN-S1065',
            name: 'Swing Check Valve',
            description: 'Bolted bonnet swing check valve',
            category: 'valves',
            manufacturer: 'Velan',
            unitCost: 2800.00,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['pump', 'compressor'],
            failureModes: ['seat_wear', 'disc_damage', 'hinge_pin_wear'],
            averageLifespan: { value: 20, unit: 'years' },
            specifications: { size: '6 inch', class: '300', material: 'CS' },
          },
        ],
      },
      {
        id: 'gaskets_seals',
        name: 'Gaskets and Seals',
        description: 'Flange gaskets, O-rings, and sealing materials',
        parts: [
          {
            id: 'spiral_wound_gasket',
            partNumber: 'FLEXITALLIC-CG',
            name: 'Spiral Wound Gasket',
            description: 'SS316L/Graphite spiral wound with inner and outer ring',
            category: 'gaskets_seals',
            manufacturer: 'Flexitallic',
            unitCost: 85.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['reactor', 'distillation_column', 'heat_exchanger', 'pump', 'compressor'],
            failureModes: ['blowout', 'leakage', 'deterioration'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { size: '4 inch', class: '300', filler: 'Graphite' },
          },
          {
            id: 'rtj_gasket',
            partNumber: 'API-R45',
            name: 'Ring Type Joint Gasket',
            description: 'Soft iron RTJ gasket for high-pressure service',
            category: 'gaskets_seals',
            manufacturer: 'James Walker',
            unitCost: 120.00,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['reactor', 'compressor'],
            failureModes: ['leakage', 'extrusion', 'corrosion'],
            averageLifespan: { value: 10, unit: 'years' },
            specifications: { ring: 'R45', material: 'Soft Iron', class: '900' },
          },
          {
            id: 'ptfe_gasket',
            partNumber: 'GARLOCK-GYLON-3510',
            name: 'PTFE Envelope Gasket',
            description: 'PTFE envelope gasket for corrosive service',
            category: 'gaskets_seals',
            manufacturer: 'Garlock',
            unitCost: 65.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['heat_exchanger', 'pump', 'storage_tank'],
            failureModes: ['cold_flow', 'leakage', 'chemical_attack'],
            averageLifespan: { value: 3, unit: 'years' },
            specifications: { size: '6 inch', class: '150', material: 'PTFE' },
          },
          {
            id: 'viton_oring',
            partNumber: 'PARKER-2-228-V75',
            name: 'Viton O-Ring',
            description: 'FKM O-ring for chemical resistance',
            category: 'gaskets_seals',
            manufacturer: 'Parker',
            unitCost: 12.00,
            currency: 'USD',
            leadTime: { value: 1, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['pump', 'compressor', 'reactor'],
            failureModes: ['extrusion', 'hardening', 'chemical_attack'],
            averageLifespan: { value: 2, unit: 'years' },
            specifications: { size: '2-228', durometer: '75A', material: 'Viton' },
          },
        ],
      },
    ],
  },

  // =============================================================================
  // ROOT CAUSE INTELLIGENCE (Chemicals Industry)
  // =============================================================================
  rootCauseLibrary: {
    categories: [
      {
        id: 'corrosion',
        name: 'Corrosion',
        description: 'Root causes related to material degradation from chemical attack',
        icon: 'Droplets',
        color: '#f59e0b',
        rootCauses: [
          {
            id: 'internal_corrosion',
            name: 'Internal Corrosion',
            description: 'Material loss due to process fluid attack on vessel or piping internals',
            category: 'corrosion',
            symptoms: [
              'Wall thickness reduction detected by UT',
              'Increased metal ion concentration in product',
              'Visible pitting or general corrosion during inspection',
              'Unexpected pressure drop in piping',
              'Corrosion product accumulation',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review corrosion monitoring data (coupons, probes, ER)',
                expectedResult: 'Corrosion rate trending or exceedance identified',
                tools: ['Corrosion monitoring system', 'Lab analysis'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Perform ultrasonic thickness measurements',
                expectedResult: 'Wall loss quantified and mapped',
                tools: ['UT thickness gauge'],
                duration: 60,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Analyze process chemistry and upset history',
                expectedResult: 'Identify corrosive species or upset conditions',
                tools: ['Process historian', 'Lab analysis'],
                duration: 45,
                skillLevel: 'advanced',
              },
              {
                order: 4,
                action: 'Conduct metallurgical analysis if required',
                expectedResult: 'Identify corrosion mechanism',
                tools: ['Metallurgical lab'],
                duration: 120,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Implement corrosion inhibitor injection',
              'Control process chemistry within limits',
              'Upgrade metallurgy for critical areas',
              'Install real-time corrosion monitoring',
              'Conduct regular inspection per API 510/570',
            ],
            typicalProbability: 0.20,
            affectedComponents: ['vessels', 'piping', 'heat_exchangers', 'pumps'],
            requiredExpertise: ['sme', 'technician'],
            estimatedResolutionTime: { min: 8, typical: 40, max: 168, unit: 'hours' },
            costRange: { min: 5000, max: 500000, currency: 'USD' },
            industryReferences: ['API 571', 'NACE SP0775'],
          },
          {
            id: 'under_deposit_corrosion',
            name: 'Under-Deposit Corrosion',
            description: 'Localized corrosion occurring beneath deposits or scale',
            category: 'corrosion',
            symptoms: [
              'Localized pitting near deposit accumulation',
              'Differential corrosion patterns',
              'Heat transfer degradation',
              'Unexpected metal loss in low-flow areas',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Inspect for deposit accumulation during turnaround',
                expectedResult: 'Identify deposit location and composition',
                tools: ['Visual inspection', 'Borescope'],
                duration: 60,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Analyze deposit composition',
                expectedResult: 'Identify deposit source and corrosive species',
                tools: ['Lab analysis', 'XRF'],
                duration: 30,
                skillLevel: 'advanced',
              },
              {
                order: 3,
                action: 'Map corrosion pattern beneath deposits',
                expectedResult: 'Quantify damage extent',
                tools: ['UT gauge', 'Pit gauge'],
                duration: 45,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Implement regular cleaning schedules',
              'Optimize flow velocities to prevent deposition',
              'Use chemical treatments to prevent scaling',
              'Install appropriate filtration',
              'Consider lining or coating critical areas',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['heat_exchangers', 'vessels', 'piping'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 24, typical: 72, max: 240, unit: 'hours' },
            costRange: { min: 10000, max: 200000, currency: 'USD' },
            industryReferences: ['API 571', 'NACE'],
          },
        ],
      },
      {
        id: 'fouling',
        name: 'Fouling',
        description: 'Root causes related to deposit buildup affecting heat transfer and flow',
        icon: 'Layers',
        color: '#8b5cf6',
        rootCauses: [
          {
            id: 'heat_exchanger_fouling',
            name: 'Heat Exchanger Fouling',
            description: 'Deposit buildup reducing heat transfer efficiency',
            category: 'fouling',
            symptoms: [
              'Decreased heat transfer coefficient',
              'Increased pressure drop across exchanger',
              'Higher process temperatures',
              'Increased energy consumption',
              'Product quality degradation',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Calculate current U value and compare to clean',
                expectedResult: 'Quantify fouling factor',
                tools: ['Process data', 'Heat exchanger rating software'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Measure pressure drop and compare to baseline',
                expectedResult: 'Identify flow restriction severity',
                tools: ['Pressure gauges', 'Process historian'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Inspect exchanger internals if accessible',
                expectedResult: 'Visual confirmation and deposit sampling',
                tools: ['Borescope', 'Sample containers'],
                duration: 60,
                skillLevel: 'intermediate',
              },
              {
                order: 4,
                action: 'Analyze fouling deposit composition',
                expectedResult: 'Identify fouling mechanism and source',
                tools: ['Lab analysis'],
                duration: 30,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Implement anti-foulant chemical treatment',
              'Optimize operating temperatures',
              'Install online cleaning systems',
              'Schedule regular cleaning intervals',
              'Monitor fouling indicators continuously',
            ],
            typicalProbability: 0.25,
            affectedComponents: ['heat_exchangers', 'condensers', 'reboilers'],
            requiredExpertise: ['sme', 'technician'],
            estimatedResolutionTime: { min: 8, typical: 24, max: 72, unit: 'hours' },
            costRange: { min: 5000, max: 100000, currency: 'USD' },
            industryReferences: ['TEMA', 'HTRI'],
          },
          {
            id: 'catalyst_deactivation',
            name: 'Catalyst Deactivation',
            description: 'Loss of catalyst activity due to fouling, poisoning, or sintering',
            category: 'fouling',
            symptoms: [
              'Decreased conversion rate',
              'Increased reactor temperature required',
              'Product selectivity changes',
              'Increased pressure drop across catalyst bed',
              'Off-spec product',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Analyze conversion and selectivity trends',
                expectedResult: 'Quantify activity loss rate',
                tools: ['Process historian', 'Lab analysis'],
                duration: 60,
                skillLevel: 'advanced',
              },
              {
                order: 2,
                action: 'Review feedstock quality history',
                expectedResult: 'Identify potential poisons or contaminants',
                tools: ['Lab records', 'Process data'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Sample spent catalyst for analysis',
                expectedResult: 'Identify deactivation mechanism',
                tools: ['Catalyst sampling system', 'Lab analysis'],
                duration: 120,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Maintain feedstock quality within limits',
              'Optimize regeneration procedures',
              'Monitor catalyst activity indicators',
              'Install guard beds for poison removal',
              'Plan catalyst change based on activity curves',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['reactors', 'catalyst_beds'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 168, typical: 336, max: 672, unit: 'hours' },
            costRange: { min: 100000, max: 5000000, currency: 'USD' },
            industryReferences: ['Catalyst manufacturer guidelines'],
          },
        ],
      },
      {
        id: 'mechanical',
        name: 'Mechanical',
        description: 'Root causes related to mechanical failures and wear',
        icon: 'Cog',
        color: '#3b82f6',
        rootCauses: [
          {
            id: 'seal_failure',
            name: 'Mechanical Seal Failure',
            description: 'Loss of sealing integrity in pumps and rotating equipment',
            category: 'mechanical',
            symptoms: [
              'Visible leakage at seal',
              'Seal flush flow changes',
              'Barrier fluid consumption increase',
              'Process fluid in seal support system',
              'Elevated seal chamber temperature',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Check seal support system parameters',
                expectedResult: 'Verify proper flush flow, pressure, and temperature',
                tools: ['Pressure gauges', 'Flow meter', 'Temperature gauge'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Inspect seal area for visible leakage',
                expectedResult: 'Quantify leak rate and identify leak point',
                tools: ['Visual inspection', 'Gas detector'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Review operating history for upsets',
                expectedResult: 'Identify operating conditions that may have damaged seal',
                tools: ['Process historian'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 4,
                action: 'Inspect failed seal components',
                expectedResult: 'Identify failure mode (thermal, chemical, mechanical)',
                tools: ['Bench inspection', 'Microscope'],
                duration: 60,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Maintain proper seal flush conditions',
              'Monitor seal support system parameters',
              'Avoid dry running conditions',
              'Use appropriate seal materials for service',
              'Implement seal MTBF tracking',
            ],
            typicalProbability: 0.22,
            affectedComponents: ['pumps', 'compressors', 'agitators'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 4, typical: 8, max: 24, unit: 'hours' },
            costRange: { min: 2000, max: 25000, currency: 'USD' },
            industryReferences: ['API 682'],
          },
          {
            id: 'vibration_issue',
            name: 'Excessive Vibration',
            description: 'Abnormal vibration in rotating equipment causing accelerated wear',
            category: 'mechanical',
            symptoms: [
              'Elevated vibration readings',
              'Audible noise changes',
              'Bearing temperature increase',
              'Foundation or piping movement',
              'Coupling wear',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Collect vibration spectrum data',
                expectedResult: 'Identify dominant frequencies and amplitudes',
                tools: ['Vibration analyzer', 'Accelerometer'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Compare spectrum to baseline and diagnose',
                expectedResult: 'Identify vibration source (imbalance, misalignment, bearing, etc.)',
                tools: ['Vibration analysis software'],
                duration: 45,
                skillLevel: 'advanced',
              },
              {
                order: 3,
                action: 'Check alignment and foundation',
                expectedResult: 'Verify alignment within tolerance, foundation integrity',
                tools: ['Laser alignment system', 'Dial indicators'],
                duration: 60,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Implement vibration monitoring program',
              'Perform precision alignment during installation',
              'Balance rotating components',
              'Maintain proper foundation',
              'Address piping strain issues',
            ],
            typicalProbability: 0.18,
            affectedComponents: ['pumps', 'compressors', 'motors', 'bearings'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 2, typical: 8, max: 24, unit: 'hours' },
            costRange: { min: 500, max: 15000, currency: 'USD' },
            industryReferences: ['ISO 10816', 'API 610'],
          },
        ],
      },
      {
        id: 'process',
        name: 'Process',
        description: 'Root causes related to process conditions and operations',
        icon: 'Activity',
        color: '#10b981',
        rootCauses: [
          {
            id: 'temperature_excursion',
            name: 'Temperature Excursion',
            description: 'Process temperature outside normal operating range',
            category: 'process',
            symptoms: [
              'High/low temperature alarms',
              'Product quality issues',
              'Reaction rate changes',
              'Equipment damage potential',
              'Safety system activation',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review temperature trends and alarm history',
                expectedResult: 'Identify timing, magnitude, and duration of excursion',
                tools: ['Process historian', 'Alarm system'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Check temperature control loop performance',
                expectedResult: 'Verify controller operation and valve response',
                tools: ['DCS trending', 'Control loop analysis'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Investigate heat input/removal systems',
                expectedResult: 'Identify root cause (utility, fouling, control)',
                tools: ['Process data', 'Field inspection'],
                duration: 45,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Optimize control loop tuning',
              'Implement alarm management',
              'Monitor heat transfer performance',
              'Train operators on upset response',
              'Install redundant temperature measurement',
            ],
            typicalProbability: 0.20,
            affectedComponents: ['reactors', 'distillation_columns', 'heat_exchangers'],
            requiredExpertise: ['sme', 'technician'],
            estimatedResolutionTime: { min: 1, typical: 4, max: 24, unit: 'hours' },
            costRange: { min: 1000, max: 100000, currency: 'USD' },
            industryReferences: ['Process safety standards'],
          },
          {
            id: 'pressure_upset',
            name: 'Pressure Upset',
            description: 'Abnormal pressure conditions affecting process operation',
            category: 'process',
            symptoms: [
              'High/low pressure alarms',
              'Relief valve activation',
              'Flow rate changes',
              'Compressor surge',
              'Equipment overpressure potential',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review pressure trends and alarm history',
                expectedResult: 'Characterize upset timing and magnitude',
                tools: ['Process historian', 'Alarm system'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Check pressure control loop performance',
                expectedResult: 'Verify controller and valve operation',
                tools: ['DCS trending'],
                duration: 20,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Evaluate system hydraulics',
                expectedResult: 'Identify blockages, restrictions, or capacity issues',
                tools: ['Process simulation', 'Field inspection'],
                duration: 60,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Maintain pressure control loops',
              'Test relief devices regularly',
              'Monitor for restrictions and blockages',
              'Implement pressure trending and alarming',
              'Train operators on pressure upset response',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['reactors', 'compressors', 'distillation_columns', 'storage_tanks'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 1, typical: 6, max: 48, unit: 'hours' },
            costRange: { min: 2000, max: 200000, currency: 'USD' },
            industryReferences: ['API 521', 'ASME standards'],
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'fouling_cascade',
        name: 'Fouling-Corrosion Cascade',
        description: 'Progression from fouling to under-deposit corrosion to failure',
        sequence: [
          'Deposit accumulation begins',
          'Heat transfer efficiency decreases',
          'Local hot spots or stagnant areas form',
          'Under-deposit corrosion initiates',
          'Wall thinning progresses',
          'Leak or failure occurs',
        ],
        indicators: [
          'Gradual heat transfer coefficient decline',
          'Increasing pressure drop',
          'Localized temperature anomalies',
        ],
        preventionStrategy: 'Implement regular cleaning and corrosion monitoring under deposits',
      },
      {
        id: 'seal_cascade',
        name: 'Seal Failure Cascade',
        description: 'Progression from seal degradation to pump failure',
        sequence: [
          'Seal face wear begins',
          'Minor leakage starts',
          'Process fluid contaminates bearing system',
          'Bearing degradation accelerates',
          'Vibration increases',
          'Pump failure',
        ],
        indicators: [
          'Barrier fluid consumption increase',
          'Seal flush temperature rise',
          'Minor vibration increase',
        ],
        preventionStrategy: 'Monitor seal support system and implement predictive maintenance',
      },
    ],
  },

  // =============================================================================
  // LABOR RATES (Chemicals Industry)
  // =============================================================================
  laborRates: {
    roles: [
      { roleId: 'process_engineer', name: 'Process Engineer', hourlyRate: 85, currency: 'USD', certifications: ['PE License', 'Six Sigma'] },
      { roleId: 'instrument_technician', name: 'Instrument Technician', hourlyRate: 55, currency: 'USD', certifications: ['ISA CCST', 'DCS Certification'] },
      { roleId: 'rotating_equipment_technician', name: 'Rotating Equipment Technician', hourlyRate: 52, currency: 'USD', certifications: ['Vibration Analysis', 'Pump Certification'] },
      { roleId: 'reliability_engineer', name: 'Reliability Engineer', hourlyRate: 90, currency: 'USD', certifications: ['CRE', 'CMRP'] },
      { roleId: 'control_room_operator', name: 'Control Room Operator', hourlyRate: 45, currency: 'USD', certifications: ['Process Operator Certification'] },
      { roleId: 'maintenance_planner', name: 'Maintenance Planner', hourlyRate: 58, currency: 'USD', certifications: ['CMMS Certification', 'Planning/Scheduling'] },
      { roleId: 'contractor_craftsman', name: 'Contract Craftsman', hourlyRate: 55, currency: 'USD' },
      { roleId: 'turnaround_labor', name: 'Turnaround Labor', hourlyRate: 48, currency: 'USD' },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.0,
    weekendMultiplier: 1.5,
  },
}
